import {Card, DataStatus} from 'platform/components';
import {
  Box,
  Center,
  Grid,
  type Integer,
  Text,
  type ValueByDevice,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil, mean, zip} from 'ramda';

import {useGetArticleStatisticsQuery} from '@dms/api/reporting';
import {
  CHART_PALETTE,
  CHART_PLOT_HEIGHT,
  ChartLegendContent,
  ChartSummaryCards,
  ChartSummaryToggles,
  ChartTooltipContent,
  useChartColors,
  useChartStyle,
  useValueAxis,
} from '@dms/features/report-charts';
import i18n from '@dms/i18n';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

import {getAnnualPartUsage} from './getAnnualPartUsage';
import {StatisticsAbcXyz} from './StatisticsAbcXyz';
import {StatisticsAging} from './StatisticsAging';
import {StatisticsChannels} from './StatisticsChannels';
import {StatisticsKpis} from './StatisticsKpis';

const CLOSED_MONTHS_PER_YEAR = 12;
const PREVIOUS_PERIOD = 'previous';
const LATEST_PERIOD = 'latest';
// Both chart cards are half the page wide from tablet up, so their summary cards take two per row.
const SUMMARY_COLUMNS: ValueByDevice<Integer> = [1, 2];
// The previous 12 months are the comparison, so they take the lightest shade of the hue of the
// latest 12 months.
const PREVIOUS_PERIOD_HUE = {
  base: CHART_PALETTE.blue.shades[2],
  summary: {
    background: CHART_PALETTE.blue.summary.background,
    border: CHART_PALETTE.blue.shades[2],
  },
};
const CHART_COLORS = {
  primary: CHART_PALETTE.blue.base,
  previous: PREVIOUS_PERIOD_HUE.base,
} as const;

interface Month {
  month: string;
  netIssuedQuantity: number | null;
  endPurchaseValue: number | null;
}

const periodOf = (months: Month[]) =>
  `${months.at(0)?.month ?? EMPTY_PLACEHOLDER} – ${months.at(-1)?.month ?? EMPTY_PLACEHOLDER}`;

// Recharts hands a tooltip item its data entry untyped. Each entry names the month of both
// periods, so the tooltip names the month that a bar shows.
const monthOfBar = (entry: unknown, periodId: unknown) =>
  match({entry, periodId})
    .with(
      {periodId: PREVIOUS_PERIOD, entry: {previousMonth: Pattern.string}},
      (bar) => bar.entry.previousMonth
    )
    .with({periodId: LATEST_PERIOD, entry: {month: Pattern.string}}, (bar) => bar.entry.month)
    .otherwise(() => undefined);

interface MonthlyIssuesChartProps {
  months: Month[];
  unit: string;
}

function MonthlyIssuesChart(props: MonthlyIssuesChartProps) {
  const [shownPeriods, setShownPeriods] = useState<string[]>([PREVIOUS_PERIOD, LATEST_PERIOD]);
  const formatNumber = useFormatNumber();
  const chartStyle = useChartStyle();
  const colors = useChartColors(CHART_COLORS);
  const previousMonths = props.months.slice(0, CLOSED_MONTHS_PER_YEAR);
  const latestMonths = props.months.slice(CLOSED_MONTHS_PER_YEAR);
  // Each of the latest 12 months stands next to the same month a year earlier.
  const rows = zip(previousMonths, latestMonths).map(([previous, latest]) => ({
    month: latest.month,
    previousMonth: previous.month,
    [PREVIOUS_PERIOD]: previous.netIssuedQuantity,
    [LATEST_PERIOD]: latest.netIssuedQuantity,
  }));
  const periods = [
    {
      id: PREVIOUS_PERIOD,
      months: previousMonths,
      color: colors.previous,
      hue: PREVIOUS_PERIOD_HUE,
    },
    {id: LATEST_PERIOD, months: latestMonths, color: colors.primary, hue: CHART_PALETTE.blue},
  ] as const;
  const formatQuantity = (value: number | null) =>
    isNil(value)
      ? EMPTY_PLACEHOLDER
      : getQuantityWithHandlingUnit(formatNumber(value, 2) ?? EMPTY_PLACEHOLDER, props.unit);

  // Only the periods switched on in the summary cards are drawn, and the value axis fits them.
  const visiblePeriods = periods.filter((period) => shownPeriods.includes(period.id));
  const valueAxis = useValueAxis(
    rows.flatMap((row) => visiblePeriods.map((period) => row[period.id])),
    (value) => formatNumber(value, 0) ?? '',
    {allowDecimals: false}
  );

  return (
    // The chart sits at the bottom of its card, so its axis lines up with the chart next to it.
    <VStack height="100%" justify="space-between" spacing={4}>
      <ChartSummaryToggles
        items={periods.map((period) => ({
          id: period.id,
          label: periodOf(period.months),
          value: formatQuantity(getAnnualPartUsage(period.months)),
          hue: period.hue,
        }))}
        selectedIds={shownPeriods}
        onChange={setShownPeriods}
        columns={SUMMARY_COLUMNS}
        isCompact
      />
      <Box width="100%" height={CHART_PLOT_HEIGHT}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={chartStyle.margin}>
            <CartesianGrid {...chartStyle.grid} />
            <XAxis dataKey="month" {...chartStyle.xAxis} />
            <YAxis {...valueAxis} />
            {/* Returns can outweigh issues, so a month can fall below 0. */}
            <ReferenceLine y={0} {...chartStyle.referenceLine} />
            <Tooltip<number, string>
              cursor={chartStyle.tooltipCursor}
              labelFormatter={() => i18n.t('entity.warehouse.reporting.monthlyIssues')}
              formatter={(value, name, item) => [
                formatQuantity(value),
                monthOfBar(item.payload, item.dataKey) ?? name,
              ]}
              content={<ChartTooltipContent />}
            />
            <Legend content={<ChartLegendContent />} />
            {visiblePeriods.map((period) => (
              <Bar
                key={period.id}
                dataKey={period.id}
                name={periodOf(period.months)}
                fill={period.color}
                radius={chartStyle.barRadius}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </VStack>
  );
}

interface ValueChartProps {
  months: Month[];
  currency: string;
}

function ValueChart(props: ValueChartProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const chartStyle = useChartStyle();
  const {primary} = useChartColors(CHART_COLORS);
  const values = props.months.map((month) => month.endPurchaseValue);
  const valueAxis = useValueAxis(values, (value) => formatNumber(value, 0) ?? '');
  // A month-end value is a stock level, so the period has an average, not a total. The API sends
  // the values of the latest 12 months only, so the average has 12 points, while the average
  // inventory quantity of the API has 13. Like that quantity, it is unknown while a month has no
  // history.
  const averageValue =
    values.length === CLOSED_MONTHS_PER_YEAR && values.every(isNotNil) ? mean(values) : null;
  return (
    // The chart sits at the bottom of its card, so its axis lines up with the chart next to it.
    <VStack height="100%" justify="space-between" spacing={4}>
      <ChartSummaryCards
        items={[
          {
            id: 'averageInventoryValue',
            label: i18n.t('entity.warehouse.reporting.averageInventoryValue'),
            value: isNil(averageValue)
              ? EMPTY_PLACEHOLDER
              : (formatCurrency(averageValue, props.currency, 2) ?? EMPTY_PLACEHOLDER),
          },
        ]}
        columns={SUMMARY_COLUMNS}
        isCompact
      />
      <Box width="100%" height={CHART_PLOT_HEIGHT}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={props.months} margin={chartStyle.margin}>
            <CartesianGrid {...chartStyle.grid} />
            <XAxis dataKey="month" {...chartStyle.xAxis} />
            <YAxis {...valueAxis} />
            <Tooltip
              cursor={chartStyle.tooltipCursor}
              formatter={(value: number) =>
                formatCurrency(value, props.currency, 2) ?? EMPTY_PLACEHOLDER
              }
              content={<ChartTooltipContent />}
            />
            <Legend content={<ChartLegendContent />} />
            <Bar
              dataKey="endPurchaseValue"
              name={i18n.t('entity.warehouse.reporting.purchaseValue')}
              fill={primary}
              radius={chartStyle.barRadius}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </VStack>
  );
}

interface StatisticsHistoryProps {
  currency: string;
  handlingUnitLabel: string;
  currentInventoryQuantity?: number;
  currentInventoryValue?: number;
  oldestStockAge?: number;
  oldestStockDate?: string;
}

export function StatisticsHistory(props: StatisticsHistoryProps) {
  const {warehouseId, articleId} = useWarehouseParams();
  const formatDate = useDateTimeFormatter();
  const {data, isLoading, isFetching, isError, refetch} = useGetArticleStatisticsQuery({
    warehouseId,
    articleId,
  });

  if (!data) {
    return (
      <VStack spacing={3}>
        <StatisticsKpis
          months={[]}
          averageInventoryQuantity={null}
          currentInventoryQuantity={props.currentInventoryQuantity}
          currentInventoryValue={props.currentInventoryValue}
          oldestStockAge={props.oldestStockAge}
          oldestStockDate={props.oldestStockDate}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
        <Center width="100%">
          <DataStatus
            isLoading={isLoading || isFetching}
            isError={isError && !isFetching}
            errorMessage={i18n.t('feature.customerInsights.sectionError')}
            errorAction={{title: i18n.t('general.actions.retry'), onClick: () => void refetch()}}
          />
        </Center>
      </VStack>
    );
  }
  const latestTwelve = data.months.slice(CLOSED_MONTHS_PER_YEAR);
  const hasIssueHistory = data.months.some(
    (month) => (month.issuedQuantity ?? 0) > 0 || (month.returnedQuantity ?? 0) > 0
  );
  const hasValueHistory = latestTwelve.some((month) => month.endPurchaseValue !== null);
  const historicalAsOf = formatDate('dateShort', parseDate(data.asOf));
  const historicalBasis = `${i18n.t('entity.warehouse.reporting.snapshotDate')}: ${historicalAsOf}`;
  // Cards in one row stretch to the tallest of them, so their frames align. Every gap between
  // cards is 12 px.
  return (
    <VStack spacing={3}>
      <StatisticsKpis
        months={data.months}
        averageInventoryQuantity={data.averageInventoryQuantity}
        historicalBasis={historicalBasis}
        currentInventoryQuantity={props.currentInventoryQuantity}
        currentInventoryValue={props.currentInventoryValue}
        oldestStockAge={props.oldestStockAge}
        oldestStockDate={props.oldestStockDate}
        currency={props.currency}
        handlingUnitLabel={props.handlingUnitLabel}
      />
      <Grid columns={[1, 2]} spacing={3}>
        <Card
          title={i18n.t('entity.warehouse.reporting.monthlyIssues')}
          parameters={[historicalBasis]}
          isFullHeight
        >
          {hasIssueHistory ? (
            <MonthlyIssuesChart months={data.months} unit={props.handlingUnitLabel} />
          ) : (
            <Text>{i18n.t('entity.warehouse.reporting.noIssueHistory')}</Text>
          )}
        </Card>
        <Card
          title={i18n.t('page.warehouse.labels.monthlyInventoryClosing')}
          parameters={[historicalBasis]}
          isFullHeight
        >
          {hasValueHistory ? (
            <ValueChart months={latestTwelve} currency={props.currency} />
          ) : (
            <Text>{i18n.t('entity.warehouse.reporting.inventoryTurnoverIncompleteSeries')}</Text>
          )}
        </Card>
      </Grid>
      <Grid columns={[1, 2]} spacing={3}>
        <StatisticsAbcXyz
          classification={data.abcXyz}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
        <StatisticsAging
          buckets={data.stockAging}
          currency={props.currency}
          handlingUnitLabel={props.handlingUnitLabel}
        />
      </Grid>
      <StatisticsChannels
        channels={data.issueChannels}
        historicalBasis={historicalBasis}
        handlingUnitLabel={props.handlingUnitLabel}
      />
    </VStack>
  );
}
